import React, { useCallback, useEffect, useState } from 'react';

import { NumberInput } from '@rbilabs/components-library/build/components/number-input';
import { useIntl } from 'react-intl';

import { useRedeemReward } from 'components/cart-item/redeem-reward/use-redeem-reward';
import { ItemUnavailableMessage } from 'components/item-unavailable-message';
import { useGetUrlSearchParam } from 'hooks/location/use-get-url-search-param';
import { useProductPrice } from 'hooks/menu/use-product-price';
import { useLoyaltyRewardsList } from 'hooks/use-loyalty-rewards-list';
import { useCdpContext } from 'state/cdp';
import { ClickEventComponentNames, CustomEventNames, EventTypes } from 'state/cdp/constants';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { useIsLoyaltyEnabled } from 'state/loyalty/hooks/use-is-loyalty-enabled';
import { useLoyaltyRewards } from 'state/loyalty/hooks/use-loyalty-rewards';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { isDiscountReward } from 'state/loyalty/types';
import { useMenuContext } from 'state/menu';
import { useOrderContext } from 'state/order';
import { useProductWizardContext } from 'state/product-wizard';
import { useEditingCartEntry } from 'state/product-wizard/hooks/use-editing-cart-entry';
import { MIN_CART_QUANTITY, getMenuObjectCartQuantity } from 'utils/cart';

import { StyledActionButton, StyledNumberInputWrapper } from '../styled';
import { useProductAvailability } from '../use-product-availability';
import { useProductCart } from '../use-product-cart';

import { ProductCtaWrapper } from './product-cta-wrapper';

export const ProductHomeCta: React.FC = () => {
  const { isOffer } = useMenuContext();
  const loyaltyEnabled = useIsLoyaltyEnabled();
  const { loyaltyUser } = useLoyaltyUser();
  const { cartEntries } = useOrderContext();
  const { productQuantity, selectedProduct, setProductQuantity } = useProductWizardContext();

  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const { addToOrder } = useProductCart();
  const editingCartEntry = useEditingCartEntry();
  const cartIdEditing = editingCartEntry?.cartId;

  const { totalPrice, formattedProductPrice } = useProductPrice({
    shouldIncludeModifications: true,
    shouldIncludeQuantity: true,
  });
  const { dayParts, isItemAvailableForDaypart, itemDayParts } = useProductAvailability();

  const { incentiveNotInMenu, limitPerOrderMet, rewardPointCost } = useRedeemReward();
  const rewardRedemptionBenefitIdParam = useGetUrlSearchParam('reward');
  const rewardIdParam = useGetUrlSearchParam('rewardId');
  const appliedLoyaltyRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);
  const stagedCartPoints = useAppSelector(selectors.loyalty.selectStagedCartPoints);
  const { rewardLimitePerOrder, isLimitRewardRedemptionPerOrderReached } =
    useLoyaltyRewards(loyaltyUser);
  const { sanityRewardsMap } = useLoyaltyRewardsList();

  const [addingToOrder, setAddingToOrder] = useState(false);

  const { availableCartQuantity, maxCartQuantityMet: isLimitReached } = getMenuObjectCartQuantity({
    menuObject: selectedProduct,
    quantityToAdd: productQuantity,
    cartEntries,
    cartIdEditing,
  });
  const maxCartQuantityMet = isLimitReached && !editingCartEntry;

  const cdp = useCdpContext();
  const handleQuantityUpdate = useCallback(
    (value: number) => {
      cdp.trackEvent({
        name: CustomEventNames.CLICK_EVENT,
        type: EventTypes.Navigation,
        attributes: {
          component: ClickEventComponentNames.BUTTON,
          text: 'Product Incrementor',
        },
      });
      const rewardApplied = appliedLoyaltyRewards[cartIdEditing];
      if (value < productQuantity && rewardApplied?.timesApplied > 1) {
        dispatch(
          actions.loyalty.unApplyReward({
            rewardBenefitId: rewardApplied.rewardId,
            cartId: cartIdEditing,
            loyaltyUser,
          })
        );
      }
      setProductQuantity(value);
    },
    [
      appliedLoyaltyRewards,
      cartIdEditing,
      dispatch,
      loyaltyUser,
      cdp,
      productQuantity,
      setProductQuantity,
    ]
  );

  // If a loyalty incentive (offer, reward) is not available as a regular menu item,
  // the incrementor also increments incentive redemptions in addition to adjusting quantity.
  // This takes into account user point and determines whether there are
  // sufficient points for further redemptions.
  const isIncrementDisabledForRewardRedemption = () => {
    if (limitPerOrderMet) {
      return true;
    }
    if (!incentiveNotInMenu || !loyaltyEnabled) {
      return false;
    }

    if (rewardRedemptionBenefitIdParam) {
      return productQuantity === rewardLimitePerOrder;
    }

    const checkRedemptionEligibility = () => {
      if (rewardRedemptionBenefitIdParam) {
        const incrementedPointCost = rewardPointCost * (productQuantity + 1);
        return stagedCartPoints <= incrementedPointCost;
      }
      return false;
    };
    return !!cartIdEditing || checkRedemptionEligibility();
  };

  const isDisableForRewardRedemption = rewardRedemptionBenefitIdParam
    ? isLimitRewardRedemptionPerOrderReached
    : false;

  const sanityReward = rewardIdParam ? sanityRewardsMap?.[rewardIdParam] : null;
  const isRewardDiscount = isDiscountReward(sanityReward);

  const showIncrementor =
    !isOffer && !maxCartQuantityMet && isItemAvailableForDaypart && !isRewardDiscount;
  const disableIncrementQuantity = maxCartQuantityMet || isIncrementDisabledForRewardRedemption();

  const maxRewardToRedeem = rewardLimitePerOrder || availableCartQuantity;

  const disableIncrementor =
    maxCartQuantityMet || (loyaltyEnabled && incentiveNotInMenu && !!cartIdEditing);

  // If price is not set correctly, don't allow adding such an item to cart, except offers
  const isCurrentPriceValid = totalPrice() > 0 || isOffer;
  const productName = selectedProduct?.name?.locale;

  const messageId = editingCartEntry
    ? 'updateQuantityAndItemWithPrice'
    : 'addQuantityAndItemWithPrice';

  const ariaLabel = formatMessage(
    { id: messageId },
    { itemName: productName, quantity: productQuantity, price: formattedProductPrice }
  );

  const addToOrderText = formatMessage({
    id: editingCartEntry ? 'update' : 'addToCart',
  });

  const label = maxCartQuantityMet
    ? formatMessage({ id: 'limitReached' })
    : `${addToOrderText} - ${formattedProductPrice}`;

  const handleClick = () => {
    cdp.trackEvent({
      name: CustomEventNames.CLICK_EVENT,
      type: EventTypes.Navigation,
      attributes: {
        component: ClickEventComponentNames.BUTTON,
        text: editingCartEntry ? 'Product Update' : 'Product Addition',
      },
    });
    // This ensures that the item is only added once even if the user clicks multiple times
    setAddingToOrder(true);
  };

  useEffect(() => {
    if (addingToOrder) {
      addToOrder();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addingToOrder]);

  return (
    <ProductCtaWrapper
      button={
        isItemAvailableForDaypart ? (
          <StyledActionButton
            aria-label={ariaLabel}
            data-testid="product-home-action-button"
            disabled={
              !isCurrentPriceValid ||
              maxCartQuantityMet ||
              isDisableForRewardRedemption ||
              addingToOrder
            }
            onClick={handleClick}
            fullWidth
            maintainWidth
          >
            {label}
          </StyledActionButton>
        ) : (
          <ItemUnavailableMessage
            dayParts={dayParts}
            itemDayParts={itemDayParts}
            marginAndBorders={false}
          />
        )
      }
      incrementor={
        showIncrementor && (
          <StyledNumberInputWrapper>
            <NumberInput
              aria-label={`Quantity ${productQuantity} of ${productName}`}
              value={productQuantity}
              onChange={handleQuantityUpdate}
              min={MIN_CART_QUANTITY}
              // specific check for reward limit
              max={!!rewardRedemptionBenefitIdParam ? maxRewardToRedeem : availableCartQuantity}
              disableIncrement={disableIncrementQuantity}
              disabled={disableIncrementor}
              decrement-aria-label={`decrement-${productName}`}
              increment-aria-label={`increment-${productName}`}
            />
          </StyledNumberInputWrapper>
        )
      }
    />
  );
};
